import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {WizardDataService} from "../../../services/wizard-data.service";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {MatListOption, MatSelectionList} from "@angular/material/list";
import {MatButton} from "@angular/material/button";
import {NgForOf, NgIf} from "@angular/common";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {Subscription} from "rxjs";


interface Language {
    code: string;
    label: string;
}

interface AiModel {
    id: string;
    name: string;
}

@Component({
    selector: 'app-step-one-config',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatInput,
        MatOption,
        MatSelect,
        MatSelectionList,
        MatListOption,
        MatError,
        MatButton,
        NgForOf,
        NgIf,
        MatLabel,
        MatCheckbox
    ],
    templateUrl: './step-one-config.component.html',
    styleUrl: './step-one-config.component.scss'
})
export class StepOneConfigComponent implements OnInit, OnDestroy {

    @Output() completed = new EventEmitter<void>();

    configForm: FormGroup;
    allLanguages: Language[] = [
        { code: 'en', label: 'English' },
        { code: 'es', label: 'Español' },
        { code: 'ar', label: 'العربية' },
        { code: 'ko', label: '한국어' },
        { code: 'hi', label: 'हिंदी' },
        { code: 'it', label: 'Italiano' },
        { code: 'ja', label: '日本語' },
        { code: 'bn', label: 'বাংলা' },
        { code: 'ro', label: 'Română' },
        { code: 'cs', label: 'Čeština' },
        { code: 'pl', label: 'Polski' },
        { code: 'zh', label: '中文' },
        { code: 'vi', label: 'Tiếng Việt' },
        { code: 'fr', label: 'Français' },
        { code: 'de', label: 'Deutsch' },
    ];
    availableModels: AiModel[] = [
        { id: 'google/gemini-3.1-flash-lite-preview', name: 'Gemini 3.1 Lite (Recommended)' },
        { id: 'google/gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro' },
        { id: 'openai/gpt-4o', name: 'GPT-4o' },
        { id: 'google/gemini-2.5-flash', name: 'Google: Gemini 2.5 Flash' },
        { id: 'google/gemini-2.5-pro', name: 'Google: Gemini 2.5 Pro' },
        { id: 'openai/gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
        { id: 'google/gemini-pro', name: 'Gemini Pro' },
        { id: 'anthropic/claude-3-haiku', name: 'Claude 3 Haiku (Fastest)' },
        { id: 'anthropic/claude-3-sonnet', name: 'Claude 3 Sonnet' },
    ];

    allLanguagesSelected: boolean = false;
    isIndeterminate: boolean = false;
    private languageSub: Subscription;

    constructor(
        private _formBuilder: FormBuilder,
        public wizardData: WizardDataService
    ) {}

    ngOnInit(): void {
        this.configForm = this._formBuilder.group({
            apiKey: [this.wizardData.apiKey, Validators.required],
            selectedModel: [this.wizardData.selectedModel, Validators.required],
            targetLanguages: [this.wizardData.targetLanguages, [Validators.required, Validators.minLength(1)]],
        });

        this.updateSelectAllState();
        this.languageSub = this.configForm.get('targetLanguages').valueChanges.subscribe(() => {
            this.updateSelectAllState();
        });
    }

    ngOnDestroy(): void {
        if (this.languageSub) {
            this.languageSub.unsubscribe();
        }
    }

    get targetLanguageOptions(): Language[] {
        return this.allLanguages.filter(lang => lang.code !== this.wizardData.baseLanguage);
    }

    toggleSelectAll(event: MatCheckboxChange): void {
        const targetLanguagesControl = this.configForm.get('targetLanguages');
        if (event.checked) {
            const allLanguageCodes = this.targetLanguageOptions.map(lang => lang.code);
            targetLanguagesControl?.setValue(allLanguageCodes);
        } else {
            targetLanguagesControl?.setValue([]);
        }
    }

    private updateSelectAllState(): void {
        const selected = this.configForm.get('targetLanguages')?.value || [];
        const numSelected = selected.length;
        const numTotal = this.targetLanguageOptions.length;
        this.allLanguagesSelected = numSelected === numTotal;
        this.isIndeterminate = numSelected > 0 && numSelected < numTotal;
    }

    onNext(): void {
        if (this.configForm.invalid) {
            return;
        }

        const { apiKey, selectedModel, targetLanguages } = this.configForm.value;
        this.wizardData.apiKey = apiKey;
        this.wizardData.selectedModel = selectedModel;
        this.wizardData.targetLanguages = targetLanguages;

        this.completed.emit();
    }
}
